import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Card from "../../../../components/card";
import SearchInputComponent from "../../../../components/searchInput/searchInput";
import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import { EventListRowDTO, EventWhen } from "../../../../models/EventDTO";
import S from "./strings";
import { Chip, dinero, errorText, fechaEvento, usePlantel } from "./comun";
import EventForm from "./eventForm";
import EventDetail from "./eventDetail";
import "../enrollmentRequests/filtrosMatriculas.css";

const WHEN: EventWhen[] = ["proximos", "pasados", "anulados"];

/*
	Administración › Eventos con las piezas de Matrículas / Promociones (Módulos Claros, 2026-11-01): pestañas Próximos · Pasados · Anulados
	que salen de la fecha (no de un estado puesto a mano), buscador, tabla en escritorio y tarjetas en el teléfono. El clic abre el evento.
*/
const EventsController: React.FC = () => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const { campusId, currency } = usePlantel();
	const [when, setWhen] = useState<EventWhen>("proximos");
	const [q, setQ] = useState<string>("");
	const [buscar, setBuscar] = useState<string>("");
	const [page, setPage] = useState<number>(1);
	const [data, setData] = useState<{ rows: EventListRowDTO[]; total: number; lastPage: number; tabCounts: { [k: string]: number } } | null>(null);
	const [abierto, setAbierto] = useState<number | null>(null);
	const [nuevo, setNuevo] = useState<boolean>(false);
	const [aviso, setAviso] = useState<{ text: string; ok: boolean } | null>(null);

	useEffect(() => {
		if (campusId === null || abierto !== null) return;
		let vigente = true;
		http.eventsService.list(campusId, { when, q: buscar, page, perPage: 50 })
			.then(r => vigente && setData({ rows: r.data.rows || [], total: r.data.total || 0, lastPage: r.data.lastPage || 1, tabCounts: r.data.tabCounts || {} }))
			.catch(e => { if (vigente) { setData({ rows: [], total: 0, lastPage: 1, tabCounts: {} }); setAviso({ text: errorText(e), ok: false }); } });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, when, buscar, page, abierto]);

	if (campusId === null) return <React.Fragment />;
	const notify = (text: string, ok = true) => setAviso({ text, ok });
	const avisoBox = aviso ? (
		<div className={"alert " + (aviso.ok ? "alert-success" : "alert-danger")} style={{ fontSize: 15, display: "flex", justifyContent: "space-between", gap: 10 }} role="status">
			<span>{aviso.text}</span><button type="button" className="btn-close" style={{ fontSize: 10 }} aria-label={S.cerrar} onClick={() => setAviso(null)} />
		</div>
	) : null;

	if (abierto !== null) {
		return (
			<div className={"container-fluid waModulo wau" + (movil ? " movil" : "")} style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
				{avisoBox}
				<EventDetail campusId={campusId} id={abierto} onBack={() => setAbierto(null)} notify={notify} />
			</div>
		);
	}

	const rows = data?.rows || [];
	const s = (r: EventListRowDTO) => r.summary || { charged: 0, collected: 0, pending: 0, hosts: 0, invitesSent: 0 };
	const evento = (r: EventListRowDTO) => <div style={{ minWidth: 180 }}><b>{r.name}</b><div className="wevSub">{[r.venue, S.tipos[r.type] !== "Graduación" ? S.tipos[r.type] : null].filter(Boolean).join(" · ") || "-"}</div></div>;
	const pendiente = (r: EventListRowDTO) => s(r).pending > 0.004 ? <span className="wevOro">{dinero(r.currency, s(r).pending)}</span> : <span className="wevSub">-</span>;
	const invitaciones = (r: EventListRowDTO) => s(r).hosts ? S.nDeM(s(r).invitesSent, s(r).hosts) : "-";

	return (
		<div className={"container-fluid waModulo wau" + (movil ? " movil" : "")} style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
				<h4 style={{ margin: 0 }}>{S.title}</h4>
				<button type="button" className="btn btn-primary waBotonGrande" onClick={() => setNuevo(true)}>
					<FontAwesomeIcon icon={solid("plus")} />&nbsp;{movil ? S.nuevoCorto : S.nuevo}
				</button>
			</div>
			{avisoBox}
			<Card>
				<div style={{ padding: movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
						<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
							{WHEN.map(w => (
								<li className="nav-item" key={w}>
									<button type="button" className={"nav-link btn btn-sm " + (when === w ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => { setWhen(w); setPage(1); }}>
										{S.when[w]}{w === "proximos" && data?.tabCounts?.proximos ? <span className="waTabCuenta">{data.tabCounts.proximos}</span> : null}
									</button>
								</li>
							))}
						</ul>
						<div style={movil ? { flex: "1 1 100%", position: "relative" } : { minWidth: 320, position: "relative" }}>
							<SearchInputComponent search={q} onChangeSearch={setQ} onClickSearch={() => { setBuscar(q.trim()); setPage(1); }} onClearSearch={() => { setQ(""); setBuscar(""); setPage(1); }} />
						</div>
					</div>
					<div className="waScrollX" style={{ overflowX: "auto", marginTop: 14 }}>
						{data === null ? <div className="loader" /> : movil ? (
							<div className="wevTarjetas">
								{rows.length === 0 ? <div className="wevVacio">{S.vacio}</div> : rows.map(r => (
									<div key={r.id} className="wevTarjeta" role="button" tabIndex={0} onClick={() => setAbierto(r.id)} onKeyDown={e => { if (e.key === "Enter") setAbierto(r.id); }}>
										<div className="arriba"><div style={{ minWidth: 0 }}>{evento(r)}</div>{r.status === "anulado" ? <Chip tono="oro">{S.anulado}</Chip> : null}</div>
										<div style={{ fontSize: 14, marginTop: 4 }}>{fechaEvento(r.dateTime || r.date)}</div>
										<div className="wevSub">{S.col.graduadas} {S.deCupo(r.counts.graduates, null)} · {S.col.invitados} {r.counts.guests} · {S.col.invitaciones} {invitaciones(r)}</div>
										<div style={{ fontSize: 14 }}>{S.col.cobrado} {dinero(r.currency, s(r).collected)}{s(r).pending > 0.004 ? <> · <span className="wevOro">{S.col.pendiente} {dinero(r.currency, s(r).pending)}</span></> : null}</div>
									</div>
								))}
							</div>
						) : (
							<table className="table table-hover wevLista">
								<thead><tr>
									<th style={{ width: 190 }}>{S.col.fecha}</th><th>{S.col.evento}</th><th className="wevNum">{S.col.graduadas}</th><th className="wevNum">{S.col.invitados}</th>
									<th className="wevNum">{S.col.cobrado}{currency?.code ? ` (${currency.code})` : ""}</th><th className="wevNum">{S.col.pendiente}</th><th className="wevNum">{S.col.invitaciones}</th>
								</tr></thead>
								<tbody>
									{rows.length === 0 ? <tr><td colSpan={7} className="wevVacio">{S.vacio}</td></tr> : rows.map(r => (
										<tr key={r.id} onClick={() => setAbierto(r.id)}>
											<td style={{ whiteSpace: "nowrap" }}>{fechaEvento(r.dateTime || r.date)}</td>
											<td>{evento(r)}</td>
											<td className="wevNum">{r.counts.graduates}</td>
											<td className="wevNum">{r.counts.guests}</td>
											<td className="wevNum">{dinero(r.currency, s(r).collected)}</td>
											<td className="wevNum">{pendiente(r)}</td>
											<td className="wevNum">{invitaciones(r)}</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
					{data && data.lastPage > 1 ? (
						<div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6, padding: "6px 0 10px 0" }}>
							<span className="wevSub">{S.nDeM(page, data.lastPage)}</span>
							<button type="button" className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
							<button type="button" className="btn btn-outline-secondary btn-sm" disabled={page >= data.lastPage} onClick={() => setPage(page + 1)}>›</button>
						</div>
					) : null}
				</div>
			</Card>
			{nuevo ? <EventForm campusId={campusId} evento={null} currency={currency} onClose={() => setNuevo(false)} onSaved={(e, texto) => { setNuevo(false); notify(texto); setAbierto(e.id); }} /> : null}
		</div>
	);
};

export default EventsController;
